// GTW-56: pure logic for the Customers detail panel and its save flow. No
// React and no I/O of its own (Customers.jsx injects the REST/Amplify calls),
// so every decision here is unit tested in __tests__/customerDetail.test.js.

// ---------------------------------------------------------------------------
// Errors

const MAX_ERROR_TEXT = 300;

const clip = (text) => {
  const s = String(text).trim();
  return s.length > MAX_ERROR_TEXT ? s.slice(0, MAX_ERROR_TEXT) + "…" : s;
};

// formatError(err): a short, human-readable description of a failed call, for
// snackbars AND console logs. Never log/concatenate the error object itself:
// an axios error carries config.headers.authorization (the Cognito ID token),
// and concatenating a GraphQL rejection renders "[object Object]".
//  - GraphQL: API.graphql rejects with { data, errors } -> errors[0].message
//  - axios: response.data.Error/.error/.message/.Message, or a plain-text body
//    (e.g. "Non existing Customer"), plus the HTTP status; err.message when
//    there is no response (network error)
//  - Error / string: its message
export const formatError = (err) => {
  if (err === null || err === undefined) return "Unknown error";
  if (typeof err === "string") return clip(err) || "Unknown error";
  if (Array.isArray(err.errors) && err.errors.length > 0) {
    const first = err.errors[0];
    const message = first && typeof first.message === "string" ? first.message : "";
    return clip(message) || "GraphQL error";
  }
  if (err.response) {
    const { status, data } = err.response;
    let detail = "";
    if (typeof data === "string") {
      detail = data;
    } else if (data && typeof data === "object") {
      const candidate = data.Error || data.error || data.message || data.Message;
      if (typeof candidate === "string") detail = candidate;
    }
    const text = clip(detail || err.message || "Request failed") || "Request failed";
    return status ? `${text} (HTTP ${status})` : text;
  }
  if (typeof err.message === "string" && err.message) return clip(err.message);
  return "Unknown error";
};

// AppSync's errorType when a conditional write fails: createCustomer on an id
// that already has a portal profile.
export const CONDITIONAL_CHECK_FAILED = "DynamoDB:ConditionalCheckFailedException";

export const isConditionalCheckFailed = (err) =>
  !!err &&
  Array.isArray(err.errors) &&
  err.errors.some((e) => !!e && e.errorType === CONDITIONAL_CHECK_FAILED);

// NEW-7: an update that failed because there is no item to update (AppSync's
// conditional update on a missing key, or a not-found error). Only then may a
// caller fall back to creating the item.
export const isMissingItemError = (err) =>
  isConditionalCheckFailed(err) ||
  (!!err &&
    Array.isArray(err.errors) &&
    err.errors.some(
      (e) =>
        !!e &&
        (/not\s*found/i.test(String(e.errorType || "")) ||
          /not\s*found/i.test(String(e.message || "")))
    ));

// N2: the change-history pair for one user's roles: only the selected
// customer's slice of the cross-tenant custom:Roles map, as JSON strings.
export const userRolesHistoryPair = (uneditedRoles, roles, customerId) => ({
  unEditedConfig: {
    Roles: JSON.stringify((uneditedRoles && uneditedRoles[customerId]) ?? []),
  },
  editedConfig: {
    Roles: JSON.stringify((roles && roles[customerId]) ?? []),
  },
});

// N5/NEW-6: what to tell the user after a failed OAuth app-client step, as
// i18n keys under "customers." with their params, in order:
//  - oldClientDeletedId: the previous client WAS deleted (profileCleared
//    false: the portal profile still references it)
//  - createdClientId: a new client was created but could not be recorded;
//    createdClientRevoked says whether the compensating delete succeeded
export const describeAppClientFailure = ({
  oldClientDeletedId,
  profileCleared,
  createdClientId,
  createdClientRevoked,
} = {}) => {
  const parts = [];
  if (oldClientDeletedId) {
    parts.push({ key: "appClientOldDeleted", params: { id: oldClientDeletedId } });
    if (profileCleared === false) {
      parts.push({
        key: "appClientProfileStillReferences",
        params: { id: oldClientDeletedId },
      });
    }
  }
  if (createdClientId) {
    parts.push({
      key: createdClientRevoked ? "appClientNewRevoked" : "appClientNewNotRevoked",
      params: { id: createdClientId },
    });
  }
  return parts;
};

// GET /configuration/customer answers 400 "Non existing Customer" when no
// Customer row is active right now (backend: now() BETWEEN StartDate AND
// EndDate) - Application-only ids, expired and half-created customers. 404 is
// treated the same. Anything else (5xx, network, auth) is a load error.
export const isMissingBackendRecord = (err) => {
  const status = err && err.response ? err.response.status : undefined;
  return status === 400 || status === 404;
};

// ---------------------------------------------------------------------------
// Building the detail customer

// The Gateway (REST) record loaded: take its REST-only fields. A response
// without an object body is treated as a load error, not as a record.
export const withBackendRecord = (row, data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return withoutBackendRecord(row, null);
  }
  return {
    ...row,
    applicationID: data.ApplicationID,
    endDate: data.EndDate,
    backendLoadFailed: false,
    backendRecordMissing: false,
  };
};

// M1: the Gateway record could not be loaded. Keep the list row (so the
// entry stays visible and inspectable) but flag it: the panel says why and
// every write from it stays disabled.
export const withoutBackendRecord = (row, err) => ({
  ...row,
  backendLoadFailed: true,
  backendRecordMissing: isMissingBackendRecord(err),
});

// Load the portal (Amplify) profile. Never throws.
// Returns { profile (item or null), loadFailed, error }.
// NEW-4: API.graphql rejects on ANY GraphQL error. A partial result (errors
// plus data.getCustomer) can't be trusted - e.g. a null service item - and a
// save from it could wipe the customer's services, so it counts as a failed
// load: the detail is flagged and Save stays disabled.
export const loadCustomerProfile = async (runQuery) => {
  try {
    const result = await runQuery();
    const profile = result && result.data ? result.data.getCustomer : null;
    return { profile: profile || null, loadFailed: false, error: null };
  } catch (err) {
    return { profile: null, loadFailed: true, error: err };
  }
};

// NEW-4: the checked service ids of a profile's service tree (4 levels), null
// items skipped at every level.
export const collectServiceIds = (services) => {
  const ids = [];
  const walk = (items, depth) => {
    (Array.isArray(items) ? items : []).filter(Boolean).forEach((item) => {
      ids.push(item.id);
      if (depth < 3) walk(item.children, depth + 1);
    });
  };
  walk(services, 0);
  return ids;
};

// NEW-3: the service catalogue (the admin profile's services without "Admin"),
// or null when it can't be used - then saving is blocked, because a save
// without the catalogue would send services: [] and wipe the customer's.
export const serviceCatalogueFromProfile = (profile) => {
  if (!profile || !Array.isArray(profile.services)) return null;
  return profile.services.filter((service) => !!service && service.name !== "Admin");
};

// Merge the full profile into the detail customer. The REST-only fields
// (applicationID, endDate) and the Gateway load flags are not part of the
// profile and are kept.
export const mergeProfileIntoCustomer = (base, profile) => ({
  ...base,
  ...profile,
  applicationID: base.applicationID,
  endDate: base.endDate,
  backendLoadFailed: !!base.backendLoadFailed,
  backendRecordMissing: !!base.backendRecordMissing,
  appClientID: profile.appClientID || base.appClientID,
  scopesRevoked: !!profile.scopesRevoked,
  hasPortalProfile: true,
  detailLoadFailed: false,
});

// No profile: empty defaults. A failed lookup says nothing about whether a
// profile exists, so hasPortalProfile is only set to false for a real "none".
export const customerWithoutProfile = (base, loadFailed) => ({
  ...base,
  ssoDomain: base.ssoDomain ?? null,
  selfValidation: base.selfValidation ?? false,
  errorNotificationAddress: base.errorNotificationAddress ?? "",
  partner: base.partner ?? "",
  scopesRevoked: false,
  hasPortalProfile: loadFailed ? base.hasPortalProfile : false,
  detailLoadFailed: !!loadFailed,
});

// ---------------------------------------------------------------------------
// What the panel may do. Every reason is an i18n key under "customers.".

export const REASONS = {
  DETAIL_LOADING: "detailLoading",
  BACKEND_RECORD_UNAVAILABLE: "backendRecordUnavailable",
  BACKEND_LOAD_ERROR: "backendLoadError",
  DETAIL_LOAD_FAILED: "detailLoadFailed",
  PROFILE_REQUIRED: "profileRequired",
  NETSUITE_ID_REQUIRED: "netsuiteIdRequired",
  END_DATE_REQUIRED: "endDateRequired",
  NO_PORTAL_PROFILE: "noPortalProfile",
  SERVICES_UNAVAILABLE: "servicesUnavailable",
  TEMPLATE_SERVICES_LOADING: "templateServicesLoading",
  TEMPLATE_SERVICES_FAILED: "templateServicesFailed",
};

// NEW-G: load states of the service catalogue and of a template's services.
export const LOAD_STATES = {
  IDLE: "idle",
  LOADING: "loading",
  LOADED: "loaded",
  FAILED: "failed",
};

// NEW-G: why "Add customer" can't be submitted yet.
//  - From a template: the template's services must be loaded (they give the
//    new customer its services and document sub-types, for every
//    application); while loading or after a failed load the customer would
//    be created with none.
//  - IVS without a template: the portal profile is created from the service
//    catalogue (Receive / Send, "Is template" too), so it must be loaded.
export const getAddCustomerBlockReason = ({
  applicationId,
  templateSelected,
  allServicesState,
  templateServicesState,
} = {}) => {
  if (templateSelected) {
    if (templateServicesState === LOAD_STATES.FAILED) {
      return REASONS.TEMPLATE_SERVICES_FAILED;
    }
    if (templateServicesState !== LOAD_STATES.LOADED) {
      return REASONS.TEMPLATE_SERVICES_LOADING;
    }
    return null;
  }
  if (applicationId === "IVS" && allServicesState !== LOAD_STATES.LOADED) {
    return REASONS.SERVICES_UNAVAILABLE;
  }
  return null;
};

// Why the detail can't be trusted as loaded; blocks every write from the
// panel. A null customer counts as still loading.
export const getLoadBlockReason = (customer, detailLoading) => {
  if (detailLoading || !customer) return REASONS.DETAIL_LOADING;
  if (customer.backendLoadFailed) {
    return customer.backendRecordMissing
      ? REASONS.BACKEND_RECORD_UNAVAILABLE
      : REASONS.BACKEND_LOAD_ERROR;
  }
  if (customer.detailLoadFailed) return REASONS.DETAIL_LOAD_FAILED;
  return null;
};

// What disables the "Save customer" button: a load problem, or (NEW-3) no
// service catalogue - a save without it would wipe the customer's services.
export const getSaveDisabledReason = (
  customer,
  detailLoading,
  allServicesLoaded = true
) => {
  const loadReason = getLoadBlockReason(customer, detailLoading);
  if (loadReason) return loadReason;
  if (!allServicesLoaded) return REASONS.SERVICES_UNAVAILABLE;
  return null;
};

// L7: the click-time guard of "Save customer". Load problems come first, so
// the user sees the real reason instead of e.g. "Netsuite ID is required".
export const getSaveBlockReason = (
  customer,
  detailLoading,
  allServicesLoaded = true
) => {
  const disabledReason = getSaveDisabledReason(
    customer,
    detailLoading,
    allServicesLoaded
  );
  if (disabledReason) return disabledReason;
  if (!customer.isTemplate && !String(customer.netsuiteID ?? "").trim()) {
    return REASONS.NETSUITE_ID_REQUIRED;
  }
  if (!customer.endDate) return REASONS.END_DATE_REQUIRED;
  return null;
};

// H3/NEW-5: the OAuth client and user-rights actions write to Cognito and the
// portal profile, not to the Gateway record, so they are gated on PROFILE
// readiness only: not loading, profile loaded and existing, catalogue loaded.
// A missing or failed Gateway record (Application-only / expired ids) does not
// block them.
export const getProfileActionBlockReason = (
  customer,
  detailLoading,
  allServicesLoaded = true
) => {
  if (detailLoading || !customer) return REASONS.DETAIL_LOADING;
  if (customer.detailLoadFailed) return REASONS.DETAIL_LOAD_FAILED;
  if (customer.hasPortalProfile !== true) return REASONS.PROFILE_REQUIRED;
  if (!allServicesLoaded) return REASONS.SERVICES_UNAVAILABLE;
  return null;
};

// The one inline notice of the detail panel, most fundamental first. The
// "no portal profile yet - saving creates it" hint is only shown when saving
// can actually create it.
export const getDetailNotice = (customer) => {
  if (!customer) return null;
  const loadReason = getLoadBlockReason(customer, false);
  if (loadReason) return { reason: loadReason, severity: "warning" };
  if (customer.hasPortalProfile === false) {
    return { reason: REASONS.NO_PORTAL_PROFILE, severity: "info" };
  }
  return null;
};

// ---------------------------------------------------------------------------
// Saving the portal profile

// L1: 0 is a valid explicit retention; only a missing value gets the default.
export const DEFAULT_RETENTION_DAYS = 90;

export const retentionDaysOrDefault = (value) => {
  if (value === null || value === undefined || value === "") {
    return DEFAULT_RETENTION_DAYS;
  }
  const days = Number(value);
  return Number.isFinite(days) ? days : DEFAULT_RETENTION_DAYS;
};

// The End date picker reports null when the field is cleared. new Date(null) is
// 1 Jan 1970, a valid Date, which slipped past the "End date required" check and
// posted an already-expired customer. Cleared or unparseable stays invalid so
// the save is blocked; a real date is taken as local midnight.
export const endDateFromPicker = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

export const PROFILE_SAVE_OUTCOMES = {
  UPDATED: "updated",
  CREATED: "created",
  CREATED_MEANWHILE: "createdMeanwhile",
};

// Write the portal profile after the Gateway record was saved.
//  - hasPortalProfile !== false: updateProfile().
//  - hasPortalProfile === false: createProfile() (backfill). If that fails
//    because a profile exists now (another admin created it after this form
//    was loaded: DynamoDB:ConditionalCheckFailedException), do NOT fall back
//    to updateProfile() - that would overwrite their profile with this form's
//    services. Re-load it instead and return CREATED_MEANWHILE with the
//    profile, so the caller shows it for review. Any other create error, or a
//    re-load that finds nothing, rethrows the original create error.
// fetchProfile() resolves to the loadCustomerProfile() shape.
export const saveCustomerProfile = async ({
  hasPortalProfile,
  createProfile,
  updateProfile,
  fetchProfile,
}) => {
  if (hasPortalProfile !== false) {
    await updateProfile();
    return { outcome: PROFILE_SAVE_OUTCOMES.UPDATED, profile: null };
  }
  try {
    await createProfile();
    return { outcome: PROFILE_SAVE_OUTCOMES.CREATED, profile: null };
  } catch (createErr) {
    // Partial GraphQL error but the created item came back: it was written.
    if (createErr && createErr.data && createErr.data.createCustomer) {
      return { outcome: PROFILE_SAVE_OUTCOMES.CREATED, profile: null };
    }
    if (!isConditionalCheckFailed(createErr)) throw createErr;
    let reloaded = null;
    try {
      reloaded = await fetchProfile();
    } catch {
      reloaded = null;
    }
    if (reloaded && !reloaded.loadFailed && reloaded.profile) {
      return {
        outcome: PROFILE_SAVE_OUTCOMES.CREATED_MEANWHILE,
        profile: reloaded.profile,
      };
    }
    throw createErr;
  }
};

// ---------------------------------------------------------------------------
// Concurrency helpers

// L9a/M3: only the latest detail load may write the detail state. begin()
// hands out a token; a response whose token is no longer current is dropped.
export const createRequestTracker = () => {
  let latest = 0;
  return {
    begin() {
      latest += 1;
      return latest;
    },
    isCurrent(token) {
      return token === latest;
    },
    latest() {
      return latest;
    },
  };
};

// Pair Promise.allSettled results with the items they were started for.
export const partitionSettled = (items, results) => {
  const fulfilled = [];
  const rejected = [];
  results.forEach((result, index) => {
    if (result.status === "fulfilled") {
      fulfilled.push({ item: items[index], value: result.value });
    } else {
      rejected.push({ item: items[index], reason: result.reason });
    }
  });
  return { fulfilled, rejected };
};

// GTW-98: the backend creates Rigger credentials for new Easy1 and NextInvoice
// customers and reads config['Url'] for them (customer_factory.py
// upsert_rigger_credentials; customer.py needs_rigger_credentials). Without it
// the create fails with a bare 400 "Bad Input" after the API key was made, so
// the Add dialog asks for it for exactly these applications.
const RIGGER_URL_APPLICATIONS = ["easy1", "nextinvoice"];

export const applicationNeedsRiggerUrl = (applicationId) =>
  RIGGER_URL_APPLICATIONS.includes(String(applicationId || "").toLowerCase());

export const RIGGER_URL_ERRORS = {
  REQUIRED: "riggerUrlRequired",
  INVALID: "riggerUrlInvalid",
};

// Validate and normalise the Rigger base URL the way the backend uses it: it
// appends /identityserver/connect/token and sends the application's shared
// Rigger credentials there (customer_factory.upsert_rigger_credentials), so
// only a plain https origin is accepted: no port, path, query, fragment,
// credentials or IP address, and nothing that parses to a different host than
// the admin reads (backslash, @, non-ASCII). This is a usability guard only;
// the server-side allow-list is GTW-99. Returns { value, error } where error is
// a key of customers.* translations.
export const normalizeRiggerUrl = (raw) => {
  const text = String(raw ?? "").trim();
  if (!text) return { value: "", error: RIGGER_URL_ERRORS.REQUIRED };
  // Printable ASCII only; { } would be expanded by the backend as {ssm:...}
  // placeholders (GTW-90); \ and @ let the parsed host differ from the text.
  if (/[^\x21-\x7e]|[\\@{}]/.test(text) || text.length > 2048) {
    return { value: "", error: RIGGER_URL_ERRORS.INVALID };
  }
  let url;
  try {
    url = new URL(text);
  } catch (e) {
    return { value: "", error: RIGGER_URL_ERRORS.INVALID };
  }
  const host = url.hostname.replace(/\.$/, "");
  if (
    url.protocol !== "https:" ||
    !host.includes(".") ||
    /^\d+\.\d+\.\d+\.\d+$/.test(host) ||
    url.port ||
    url.username ||
    url.password ||
    url.pathname !== "/" ||
    url.search ||
    url.hash
  ) {
    return { value: "", error: RIGGER_URL_ERRORS.INVALID };
  }
  return { value: `https://${host}`, error: null };
};
